import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Camera, MapPin, Check, X, Circle, ArrowLeft, ClipboardCheck, Trash2, PlusCircle, Save } from 'lucide-react';

const STATUS_META = {
  positivo: { label: '✓ OK', color: 'bg-green-600 text-white', border: 'border-green-600' },
  negativo: { label: '✗ Problema', color: 'bg-red-600 text-white', border: 'border-red-600' },
  no_aplica: { label: 'N/A', color: 'bg-slate-500 text-white', border: 'border-slate-500' },
};

export default function FieldChecklist() {
  const nav = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const placeId = params.get('place_id');
  const placeName = params.get('place_name');

  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);
  const [uploadingItemId, setUploadingItemId] = useState(null);

  const loadList = async () => {
    try {
      const r = await api.get('/field-checklists', { params: placeId ? { place_id: placeId } : {} });
      setList(r.data || []);
      if (id) {
        const single = await api.get(`/field-checklists/${id}`);
        setActive(single.data);
      } else if (r.data?.[0]) {
        const single = await api.get(`/field-checklists/${r.data[0].id}`);
        setActive(single.data);
      }
    } catch { toast.error('Error cargando checklists'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadList(); }, [placeId, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createNew = async () => {
    setCreating(true);
    try {
      const r = await api.post('/field-checklists', { place_id: placeId, place_name: placeName });
      setActive(r.data);
      loadList();
      toast.success('Checklist creado');
    } catch { toast.error('Error creando checklist'); }
    finally { setCreating(false); }
  };

  const updateStatus = (itemId, newStatus) => {
    const items = active.items.map(it => it.id === itemId ? { ...it, status: it.status === newStatus ? 'pendiente' : newStatus } : it);
    setActive({ ...active, items });
  };

  const updateNotes = (itemId, notes) => {
    const items = active.items.map(it => it.id === itemId ? { ...it, notes } : it);
    setActive({ ...active, items });
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/field-checklists/${active.id}`, { items: active.items });
      setActive(r.data);
      toast.success('Guardado');
    } catch { toast.error('Error guardando'); }
    finally { setSaving(false); }
  };

  const capturePhoto = async (itemId) => {
    setUploadingItemId(itemId);
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !uploadingItemId) { setUploadingItemId(null); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error('Foto muy grande (máx 3MB)'); setUploadingItemId(null); return; }

    // Try to get geolocation
    let coords = { lat: null, lon: null };
    if (navigator.geolocation) {
      try {
        coords = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
            () => resolve({ lat: null, lon: null }),
            { timeout: 5000 }
          );
        });
      } catch { /* ignore */ }
    }

    // Compress: resize if width > 1200
    const reader = new FileReader();
    reader.onload = async () => {
      const img = new Image();
      img.onload = async () => {
        const maxW = 1200;
        let w = img.width, h = img.height;
        if (w > maxW) { h = h * maxW / w; w = maxW; }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
        const b64 = dataUrl.split(',')[1];

        try {
          await api.post(`/field-checklists/${active.id}/photos`, {
            item_id: uploadingItemId,
            data: b64, mime: 'image/jpeg',
            lat: coords.lat, lon: coords.lon,
          });
          toast.success('Foto guardada' + (coords.lat ? ` (${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)})` : ' (sin GPS)'));
          // Reload
          const r = await api.get(`/field-checklists/${active.id}`);
          setActive(r.data);
        } catch { toast.error('Error subiendo foto'); }
        finally { setUploadingItemId(null); e.target.value = ''; }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  const deletePhoto = async (itemId, photoId) => {
    try {
      await api.delete(`/field-checklists/${active.id}/photos/${photoId}`);
      const r = await api.get(`/field-checklists/${active.id}`);
      setActive(r.data);
    } catch { toast.error('Error'); }
  };

  const stats = active?.stats || {};
  const grouped = {};
  (active?.items || []).forEach(it => { (grouped[it.category] ||= []).push(it); });

  if (loading) return <div className="p-8 text-slate-500">Cargando…</div>;

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto pb-32">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-3" data-testid="back-btn">
        <ArrowLeft size={14} /> Volver
      </button>

      <div className="mb-4">
        <div className="label-caps flex items-center gap-1"><ClipboardCheck size={12} />Sprint 8 · Checklist de campo</div>
        <h1 className="font-display text-2xl md:text-4xl font-extrabold text-slate-900 mt-1">{active?.name || 'Sin visita activa'}</h1>
        {(placeName || active?.place_name) && <div className="text-sm text-slate-600 mt-1">{placeName || active?.place_name}</div>}
      </div>

      {!active ? (
        <div className="border border-dashed border-slate-300 p-8 text-center">
          <p className="text-slate-500 mb-4">No hay checklist activo{placeId ? ' para este lugar' : ''}.</p>
          <Button onClick={createNew} disabled={creating} data-testid="new-checklist-btn" className="bg-blue-900 hover:bg-blue-800">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <><PlusCircle size={14} className="mr-1" />Crear checklist nuevo</>}
          </Button>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-2 mb-6" data-testid="checklist-stats">
            <StatCell label="Completado" value={`${stats.completion_pct}%`} />
            <StatCell label="OK" value={stats.positive} color="text-green-700" />
            <StatCell label="Problemas" value={stats.negative} color="text-red-700" />
            <StatCell label="Score campo" value={stats.score_field ?? '—'} color="text-blue-900" />
          </div>

          {/* Items grouped by category */}
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="mb-5">
              <div className="label-caps mb-2">{cat}</div>
              <div className="space-y-2">
                {items.map(it => (
                  <div key={it.id} className="bg-white border border-slate-200 p-3" data-testid={`item-${it.id}`}>
                    <div className="text-sm text-slate-900 mb-2">{it.question}</div>
                    <div className="flex gap-1 mb-2">
                      {Object.entries(STATUS_META).map(([k, meta]) => (
                        <button key={k} onClick={() => updateStatus(it.id, k)} data-testid={`status-${it.id}-${k}`}
                          className={`flex-1 py-2.5 text-xs font-semibold border transition-colors ${it.status === k ? meta.color + ' ' + meta.border : 'bg-white text-slate-700 border-slate-200'}`}>
                          {meta.label}
                        </button>
                      ))}
                    </div>
                    <textarea value={it.notes || ''} onChange={(e) => updateNotes(it.id, e.target.value)}
                      placeholder="Notas / observación…" data-testid={`notes-${it.id}`}
                      className="w-full text-xs p-2 border border-slate-200 bg-slate-50 h-14 resize-none" />

                    {/* Photos */}
                    {it.photos?.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {it.photos.map(ph => (
                          <div key={ph.id} className="relative group border border-slate-200">
                            {ph.data && <img src={`data:${ph.mime};base64,${ph.data}`} alt="foto" className="w-full h-24 object-cover" />}
                            <button onClick={() => deletePhoto(it.id, ph.id)} data-testid={`delete-photo-${ph.id}`}
                              className="absolute top-1 right-1 bg-white/90 p-1 shadow hover:bg-red-100">
                              <Trash2 size={11} className="text-red-700" />
                            </button>
                            {ph.lat && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 flex items-center gap-1">
                                <MapPin size={9} /> {ph.lat.toFixed(4)}, {ph.lon.toFixed(4)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <Button onClick={() => capturePhoto(it.id)} data-testid={`photo-btn-${it.id}`}
                      variant="outline" size="sm" className="mt-2 w-full">
                      {uploadingItemId === it.id ? <Loader2 size={12} className="animate-spin" /> : <><Camera size={12} className="mr-1" />Tomar foto (con GPS)</>}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Sticky save */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 md:pl-72 z-20">
            <Button onClick={saveAll} disabled={saving} data-testid="save-btn" className="w-full max-w-3xl mx-auto bg-blue-900 hover:bg-blue-800 h-12">
              {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Guardando…</> : <><Save size={14} className="mr-2" />Guardar checklist</>}
            </Button>
          </div>

          {/* Hidden file input with capture for mobile camera */}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={handleFile} data-testid="photo-file-input" />
        </>
      )}
    </div>
  );
}

const StatCell = ({ label, value, color }) => (
  <div className="p-2 bg-white border border-slate-200 text-center">
    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className={`font-display text-xl font-bold mt-1 ${color || 'text-slate-900'}`}>{value}</div>
  </div>
);
